<template>
    <div
        class="badge-container"
        :class="[`tier-${tier}`, mode === 'indicator' ? 'mode-indicator' : 'mode-badge']"
        :style="{ '--size': `${size}px` }"
    >
        <svg
            viewBox="0 0 100 100"
            class="badge-svg"
            xmlns="http://www.w3.org/2000/svg"
            :width="size"
            :height="size"
            :aria-label="label ?? 'Nagrada'"
            role="img"
        >
            <!-- The frame (bg + rim) renders in both modes; indicator mode
                 just tints it bright orange via .mode-indicator so the flame
                 chip carries the same visual weight as the tier badges. -->
            <circle cx="50" cy="50" r="46" class="badge-bg" />
            <circle cx="50" cy="50" r="41" class="badge-rim" fill="none" stroke-width="2" />

            <template v-if="type === '2d6'">
                <!-- Back die: lower-left, unfilled so we read the rim only. -->
                <rect
                    x="20"
                    y="38"
                    width="36"
                    height="36"
                    rx="3"
                    class="die-shape die-shape--back"
                />
                <!-- Front die: upper-right, filled with bg colour so it cleanly
                     occludes the back die's stroke where they overlap. -->
                <rect
                    x="44"
                    y="22"
                    width="36"
                    height="36"
                    rx="3"
                    class="die-shape die-shape--front"
                />
            </template>

            <g v-else class="die-icon-group" transform="translate(50, 50)">
                <path
                    v-if="type === 'd4'"
                    d="M0 -34 L32 22 L-32 22 Z"
                    class="die-shape"
                />

                <rect
                    v-if="type === 'd6'"
                    x="-25"
                    y="-25"
                    width="50"
                    height="50"
                    rx="4"
                    class="die-shape"
                />

                <path v-if="type === 'd8'" d="M0 -35 L30 0 L0 35 L-30 0 Z" class="die-shape" />

                <!-- d10: hexagonal kite — pointed top/bottom, widest at the
                     equator. Reads as a distinct silhouette next to d8's
                     diamond and d12's pentagon. -->
                <path
                    v-if="type === 'd10'"
                    d="M0 -35 L26 -16 L30 6 L0 35 L-30 6 L-26 -16 Z"
                    class="die-shape"
                />

                <path
                    v-if="type === 'd12'"
                    d="M0 -32 L31 -10 L19 26 L-19 26 L-31 -10 Z"
                    class="die-shape"
                />

                <path
                    v-if="type === 'd20'"
                    d="M0 -35 L30 -17 L30 17 L0 35 L-30 17 L-30 -17 Z"
                    class="die-shape"
                />

                <path
                    v-if="type === 'crown'"
                    d="M-30 15 L-30 -15 L-15 -5 L0 -25 L15 -5 L30 -15 L30 15 Z"
                    class="die-shape"
                />

                <path
                    v-if="type === 'star'"
                    d="M0 -34 L8 -11 L32 -10 L13 4 L20 28 L0 14 L-20 28 L-13 4 L-32 -10 L-8 -11 Z"
                    class="die-shape"
                />

                <!-- Trophy: a cup closed at the top by a shallow rim curve (the
                     mouth), two side handles as separate arcs, then a stem and a
                     flared base. Outline-only like crown/star so it reads at chip
                     size. The closing rim is what makes it read as a cup. -->
                <path
                    v-if="type === 'trophy'"
                    d="M-16 -28 C-16 -6 -10 4 -4 6 L-4 14 L-16 22 L16 22 L4 14 L4 6 C10 4 16 -6 16 -28 C8 -22 -8 -22 -16 -28 Z M16 -24 C26 -22 26 -10 16 -10 M-16 -24 C-26 -22 -26 -10 -16 -10"
                    class="die-shape"
                />

                <!-- Flame silhouette: main central peak at (0,-34), one
                     larger tendril on the left around (-18,-20), one smaller
                     tendril on the right around (12,-18). The asymmetry +
                     valleys between peaks read as flame fingers at chip size.
                     Slightly shorter than the unringed version so it sits
                     cleanly inside the new bg frame. -->
                <path
                    v-if="type === 'flame'"
                    d="M0 -34 C-4 -28 -7 -24 -10 -18 C-14 -22 -20 -22 -20 -14 C-24 -6 -28 2 -26 10 C-26 24 -14 32 0 32 C14 32 26 24 26 10 C28 0 22 -8 16 -14 C16 -18 12 -18 12 -10 C8 -16 4 -26 0 -34 Z"
                    class="die-shape die-shape--flame"
                />
            </g>

            <text
                v-if="value != null"
                :x="textX"
                :y="textY"
                dominant-baseline="middle"
                text-anchor="middle"
                class="badge-text"
                :class="[textSizeClass, type === 'flame' ? 'flame-text' : null]"
            >
                {{ value }}
            </text>
        </svg>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { BadgeTier, DieSides } from '../../models/Badge';

export type AchievementBadgeType = `d${DieSides}` | 'crown' | 'star' | 'trophy' | '2d6' | 'flame';
export type AchievementBadgeMode = 'badge' | 'indicator';

const props = withDefaults(
    defineProps<{
        type: AchievementBadgeType;
        tier: BadgeTier;
        size: number;
        value?: number;
        label?: string;
        mode?: AchievementBadgeMode;
    }>(),
    {
        value: undefined,
        label: undefined,
        mode: 'badge',
    },
);

// '2d6' places its number on the front die (upper-right). All other variants
// keep the existing centred glyph.
const textX = computed(() => (props.type === '2d6' ? 62 : 50));
const textY = computed(() => {
    if (props.type === '2d6') {
        return 42;
    }
    if (props.type === 'flame') {
        // Flame's vertical centroid sits a bit below origin — nudge text down.
        return 58;
    }
    return 52;
});

const textSizeClass = computed(() => {
    if (props.value == null) {
        return null;
    }

    if (props.type === '2d6') {
        return props.value > 9 ? 'tiny-text' : 'small-text';
    }

    return props.value > 9 ? 'small-text' : null;
});
</script>

<style scoped>
.badge-container {
    width: var(--size);
    height: var(--size);
    display: inline-block;
}

/* Tier palette — kept in sync with BadgeTier in models/Badge.ts.
   Wood is a warm matte brown; bronze stays metallic orange; silver is cool;
   gold is warm yellow. The wood/silver split must read at small sizes. */
.tier-wood   { --primary: #a07e57; --secondary: #4b3621; --text: #fff5eb; }
.tier-bronze { --primary: #cd7f32; --secondary: #78350f; --text: #fff5eb; }
.tier-silver { --primary: #cbd5e1; --secondary: #334155; --text: #ffffff; }
.tier-gold   { --primary: #fbbf24; --secondary: #78350f; --text: #451a03; }

/* Indicator mode renders without the round frame and uses a fixed bright
   orange palette regardless of the tier prop — used for the current-streak
   flame on Profile. The container still gets a `tier-*` class so consumers
   can override colour if they ever need to. */
.mode-indicator { --primary: #f97316; --secondary: #7c2d12; --text: #fff5eb; }

.badge-bg {
    fill: #121212;
    stroke: var(--primary);
    stroke-width: 3;
}

.badge-rim {
    stroke: var(--primary);
    opacity: 0.3;
}

.die-shape {
    fill: none;
    stroke: var(--primary);
    stroke-width: 4;
    stroke-linejoin: round;
}

/* Flame is the only shape we want filled — empty outline looks anaemic and
   loses the silhouette at chip size. The thicker outline keeps the silhouette
   from melting into the dark badge bg on small chips. */
.die-shape--flame {
    fill: var(--primary);
    stroke: var(--secondary);
    stroke-width: 4;
    stroke-linejoin: round;
}

/* 2d6 needs the front die filled to occlude the back die where they overlap.
   Colour matches .badge-bg so the cutout looks intentional. */
.die-shape--back {
    fill: none;
    stroke-width: 3;
}

.die-shape--front {
    fill: #121212;
    stroke-width: 3;
}

.badge-text {
    fill: var(--primary);
    font-family: sans-serif;
    font-weight: 800;
    font-size: 32px;
    letter-spacing: -1px;
}

/* Inside the filled flame, the number sits on top of the primary fill.
   Light-primary tiers (silver) would render white-on-white without an outline,
   so we stroke the glyph in the dark --secondary first and fill --text on top
   — gives a readable shadow on every tier without per-tier branching. */
.badge-text.flame-text {
    fill: var(--text);
    stroke: var(--secondary);
    stroke-width: 3;
    stroke-linejoin: round;
    paint-order: stroke fill;
    font-size: 28px;
}

.badge-text.small-text {
    font-size: 24px;
}

.badge-text.tiny-text {
    font-size: 18px;
}

.tier-gold .badge-bg {
    filter: drop-shadow(0 0 2px var(--primary));
}
</style>
